import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  deleteWithTokenAPI,
  getWithTokenAPI,
  postWithoutTokenAPI,
  postWithTokenAPI,
  putWithTokenAPI,
} from "../Util/Api";
import SimpleRichEditor from "../Component/SimpleRichEditor";
import "../Style/Component/Personalinfo.css";

/* ===== LOGO MANAGER ===== */
const LogoManager = ({ token, permission, adminId }) => {
  const [applogo, setApplogo] = useState("");
  const [logoid, setLogoId] = useState("");
  const [activeType, setActiveType] = useState("user");
  const [uploading, setUploading] = useState(false);
  const logoFileRef = useRef(null);

  const fetchLogo = async () => {
    try {
      const res = await getWithTokenAPI(token, `/logo?type=${activeType}`);
      setApplogo(res?.data?.[0]?.logo || "");
      setLogoId(res?.data?.[0]?._id || "");
    } catch (error) {
      console.error("Failed to fetch logo:", error);
    }
  };

  useEffect(() => {
    fetchLogo();
  }, [activeType]);

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("files", file);

    const res = await postWithoutTokenAPI(
      `/upload_image?userId=${adminId}&type=admin`,
      formData,
      true
    );

    return res?.data?.[0]?.fileUrl;
  };

  const handleUpdatelogo = async (url) => {
    try {
      setUploading(true);
      const payload = { logo: url };
      const response = await putWithTokenAPI(
        token,
        `/logo/${logoid}?type=${activeType}`,
        payload
      );
      if (response?.statusCode === 200 || response?.status === "success") {
        toast.success(`${activeType.toUpperCase()} App Logo Updated Successfully`);
      } else {
        toast.error(response?.message || "Failed to update logo");
      }
    } catch (error) {
      console.error("Failed to update logo:", error);
      toast.error("Failed to update logo");
    } finally {
      setUploading(false);
    }
  };

  const handlelogoImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const url = await uploadImage(file);
      if (url) {
        setApplogo(url);
        await handleUpdatelogo(url);
      } else {
        toast.error("Failed to upload image");
      }
    } catch (err) {
      toast.error("Error uploading logo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="logo-manager-container">
      {/* Header */}
      <div className="logo-manager-header">
        <div className="logo-manager-title">
          <h3>App Branding & Logo Management</h3>
          <p>Manage official logos displayed across User and Driver applications.</p>
        </div>

        <div className="logo-type-selector">
          <button
            type="button"
            className={`logo-type-btn ${activeType === "user" ? "active" : ""}`}
            onClick={() => setActiveType("user")}
          >
            User App
          </button>
          <button
            type="button"
            className={`logo-type-btn ${activeType === "driver" ? "active" : ""}`}
            onClick={() => setActiveType("driver")}
          >
            Driver App
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="logo-grid">
        {/* Left: Active Logo Card */}
        <div className="logo-preview-card">
          <div className="logo-preview-header">
            <span>Current Active Logo ({activeType.toUpperCase()})</span>
            <span className="logo-badge">Active</span>
          </div>

          <div className="logo-img-wrapper">
            {applogo ? (
              <img src={applogo} alt={`${activeType} App Logo`} />
            ) : (
              <p style={{ color: "#94a3b8", fontSize: "14px" }}>No logo uploaded</p>
            )}
          </div>
        </div>

        {/* Right: Upload Dropzone */}
        <div
          className="logo-dropzone"
          onClick={() => permission?.logoandtermsCondition?.update && logoFileRef.current?.click()}
        >
          <div className="upload-icon-circle">
            {uploading ? "⏳" : "☁️"}
          </div>
          <p>{uploading ? "Uploading Logo..." : "Upload New Brand Logo"}</p>
          <span>Click to browse or upload (PNG, SVG, WEBP)</span>
          <span style={{ fontSize: "11px", marginTop: "6px", color: "#64748b" }}>
            Recommended: Transparent PNG (512x512px)
          </span>

          <input
            type="file"
            hidden
            ref={logoFileRef}
            onChange={handlelogoImage}
            accept="image/*"
          />

          {permission?.logoandtermsCondition?.update && !uploading && (
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: "16px" }}
              onClick={(e) => {
                e.stopPropagation();
                logoFileRef.current?.click();
              }}
            >
              Select Image File
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ===== POLICY LISTING ===== */
const PolicyListing = ({ list, defaultTitle }) => {
  const section = list?.sections || [];

  return (
    <div className="tc-listing">
      <h3>{list?.title || defaultTitle}</h3>
      {list?.description && (
        <p style={{ color: "#666", marginBottom: "15px" }}>{list.description}</p>
      )}

      {section.length === 0 ? (
        <p>No Sections Found</p>
      ) : (
        <div className="tc-sections">
          {section.map((item, index) => (
            <div key={index} className="tc-section">
              <h4>
                {index + 1}. {item.heading}
              </h4>

              {item.points?.map((point, i) => (
                <div
                  key={i}
                  className="tc-point"
                  dangerouslySetInnerHTML={{ __html: point }}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ===== POLICY CREATE / EDIT ===== */
const PolicyCreate = ({
  list,
  token,
  endpoint,
  policyTitle,
  activeterms,
  setTcTab,
  onRefresh,
}) => {
  const [title, setTitle] = useState(list?.title || policyTitle);
  const [description, setDescription] = useState(list?.description || "");
  const [formData, setFormData] = useState([{ title: "", description: "" }]);
  const [expandedSection, setExpandedSection] = useState(0);

  useEffect(() => {
    if (list?._id) {
      setTitle(list.title || policyTitle);
      setDescription(list.description || "");
      if (Array.isArray(list?.sections)) {
        const mapped = list.sections.map((sec) => ({
          title: sec.heading || "",
          description: Array.isArray(sec.points)
            ? sec.points.join("")
            : sec.points || "",
        }));

        setFormData(
          mapped.length > 0 ? mapped : [{ title: "", description: "" }]
        );
        setExpandedSection(0);
      }
    } else {
      setTitle(policyTitle);
      setDescription(`This is a dummy ${policyTitle.toLowerCase()}.`);
      setFormData([{ title: "", description: "" }]);
      setExpandedSection(0);
    }
  }, [list, policyTitle]);

  const toggleSection = (index) => {
    setExpandedSection((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleAddSection = () => {
    setFormData((prev) => [...prev, { title: "", description: "" }]);
    setExpandedSection(formData.length);
  };

  const handleChange = (index, field, value) => {
    const updated = [...formData];
    updated[index][field] = value;
    setFormData(updated);
  };

  const handleRemove = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
    setExpandedSection((prevIndex) => {
      if (prevIndex === index) {
        return formData.length > 1 ? Math.min(index, formData.length - 2) : null;
      } else if (prevIndex > index) {
        return prevIndex - 1;
      }
      return prevIndex;
    });
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      const response = await deleteWithTokenAPI(
        token,
        `/${endpoint}/delete/${id}`
      );

      if (response?.statusCode === 200 || response?.status) {
        toast.success(`${policyTitle} deleted successfully`);
        onRefresh && onRefresh();
        setTcTab("preview");
      } else {
        toast.error(
          response?.message || `Failed to delete ${policyTitle}`
        );
      }
    } catch (error) {
      console.error(`Error deleting ${policyTitle}:`, error);
      toast.error("Something went wrong while deleting");
    }
  };

  const handleSave = async () => {
    const sections = Array.isArray(formData)
      ? formData.map((item) => ({
          heading: item.title || "",
          points: item.description ? [item.description] : [],
        }))
      : [];

    const payload = {
      title: title || policyTitle,
      description:
        description || `This is a dummy ${policyTitle.toLowerCase()}.`,
      type: activeterms.toLowerCase(),
      sections: sections,
      version: list?.version || "1.0",
      is_active: true,
    };

    try {
      if (list?._id) {
        const response = await putWithTokenAPI(
          token,
          `/${endpoint}/update/${list._id}`,
          payload
        );

        if (response?.statusCode === 200 || response?.status === "success") {
          onRefresh && onRefresh();
          setTcTab("preview");
          toast.success(`${policyTitle} updated successfully`);
        } else {
          toast.error(response?.message || `Failed to update ${policyTitle}`);
        }
      } else {
        const response = await postWithTokenAPI(
          token,
          `/${endpoint}/create`,
          payload
        );

        if (response?.statusCode === 200 || response?.status === "success") {
          onRefresh && onRefresh();
          setTcTab("preview");
          toast.success(`${policyTitle} created successfully`);
        } else {
          toast.error(response?.message || `Failed to create ${policyTitle}`);
        }
      }
    } catch (error) {
      console.error(`Error saving ${policyTitle}:`, error);
      toast.error(`Failed to save ${policyTitle}`);
    }
  };

  return (
    <div className="editor-container minimalist">
      <div className="tc-create-container">
        <div className="tc-create-container-chiled">
          <h3>
            {list?._id ? `Edit ${policyTitle}` : `Create ${policyTitle}`}
          </h3>
          <button className="btn btn-primary" onClick={handleAddSection}>
            + Add Section
          </button>
        </div>

        <div className="form-groupp" style={{ marginTop: "10px" }}>
          <label>Policy Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="form-input-title"
          />
        </div>

        <div className="form-groupp">
          <label>Policy Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="form-input-title"
          />
        </div>

        {formData.map((item, index) => (
          <div className="tc-form-card" key={index}>
            <div
              className="accordion-section-header"
              onClick={() => toggleSection(index)}
            >
              <div className="accordion-header-left">
                <span className="accordion-toggle-icon">
                  {expandedSection === index ? "▼" : "▶"}
                </span>
                <h4>
                  Section {index + 1}
                  {item.title ? `: ${item.title}` : ""}
                </h4>
              </div>

              {formData.length > 1 && (
                <button
                  type="button"
                  className="btn btn-danger btn-small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(index);
                  }}
                >
                  Cancel
                </button>
              )}
            </div>

            {expandedSection === index && (
              <div className="accordion-section-content">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={item.title}
                    placeholder="Enter section title"
                    onChange={(e) =>
                      handleChange(index, "title", e.target.value)
                    }
                    className="form-input-title"
                  />
                </div>

                <SimpleRichEditor
                  key={index}
                  value={item.description}
                  index={index}
                  onChange={handleChange}
                />
              </div>
            )}
          </div>
        ))}

        <div
          style={{
            marginTop: 20,
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          {list?._id && (
            <button
              className="btn btn-danger delete-btn-teams"
              onClick={() => handleDelete(list?._id)}
            >
              Delete
            </button>
          )}

          <button className="btn btn-success" onClick={handleSave}>
            {list?._id ? `Update ${policyTitle}` : `Save ${policyTitle}`}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===== POLICY MANAGER ===== */
const PolicyManager = ({ policyTitle, endpoint, token, permission }) => {
  const [tcTab, setTcTab] = useState("preview");
  const [activeterms, setActiveTerms] = useState("Driver");
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const typeStr = activeterms.toLowerCase();
      const response = await getWithTokenAPI(
        token,
        `/${endpoint}/list?type=${typeStr}`
      );

      if (response?.statusCode === 200 && response?.data) {
        if (Array.isArray(response.data)) {
          const matched = response.data.find(
            (item) => item.type?.toLowerCase() === typeStr
          );
          setList(
            matched ||
              response.data[activeterms === "Driver" ? 0 : 1] ||
              response.data[0] ||
              null
          );
        } else {
          setList(response.data);
        }
      } else {
        setList(null);
      }
    } catch (error) {
      console.error(`Error fetching ${policyTitle}:`, error);
      setList(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicy();
  }, [token, activeterms, endpoint]);

  return (
    <div className="tc-wrapper">
      <div className="tc-tabs">
        <div className="tc-tabs-child">
          <span
            className={`tc-tab ${activeterms === "Driver" ? "active" : ""}`}
            onClick={() => setActiveTerms("Driver")}
          >
            Driver
          </span>
          <span
            className={`tc-tab ${activeterms === "User" ? "active" : ""}`}
            onClick={() => setActiveTerms("User")}
          >
            User
          </span>
        </div>
        <div className="tc-tabs-child">
          <span
            className={tcTab === "preview" ? "tc-tab active" : "tc-tab"}
            onClick={() => setTcTab("preview")}
          >
            Preview
          </span>

          {permission?.logoandtermsCondition?.update && (
            <span
              className={tcTab === "create" ? "tc-tab active" : "tc-tab"}
              onClick={() => setTcTab("create")}
            >
              {list?._id ? "Edit" : "Create"}
            </span>
          )}
        </div>
      </div>

      <div className="tc-content">
        {loading ? (
          <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
        ) : tcTab === "preview" ? (
          <PolicyListing list={list} defaultTitle={policyTitle} />
        ) : (
          <PolicyCreate
            list={list}
            token={token}
            endpoint={endpoint}
            policyTitle={policyTitle}
            activeterms={activeterms}
            setTcTab={setTcTab}
            onRefresh={fetchPolicy}
          />
        )}
      </div>
    </div>
  );
};

/* ===== CONTACT US LISTING ===== */
const ContactUsListing = ({ data }) => {
  if (!data)
    return (
      <div className="tc-listing">
        <p>No Contact Us details found.</p>
      </div>
    );

  return (
    <div className="contact-us-listing">
      <h3>{data.heading || "Contact Us"}</h3>
      {data.content && (
        <p style={{ color: "#555", marginBottom: "20px" }}>{data.content}</p>
      )}

      <div className="contact-card-grid">
        <div className="contact-card">
          <label>Email Address</label>
          <p>
            <a href={`mailto:${data.email}`}>{data.email || "N/A"}</a>
          </p>
        </div>

        <div className="contact-card">
          <label>Phone Number</label>
          <p>
            <a href={`tel:${data.phone}`}>{data.phone || "N/A"}</a>
          </p>
        </div>

        <div className="contact-card">
          <label>WhatsApp</label>
          <p>{data.whatsapp || "N/A"}</p>
        </div>

        <div className="contact-card">
          <label>Working Hours</label>
          <p>{data.workingHours || "N/A"}</p>
        </div>

        <div className="contact-card" style={{ gridColumn: "span 2" }}>
          <label>Address</label>
          <p>{data.address || "N/A"}</p>
        </div>

        {data.mapLink && (
          <div className="contact-card" style={{ gridColumn: "span 2" }}>
            <label>Map Link</label>
            <p>
              <a href={data.mapLink} target="_blank" rel="noopener noreferrer">
                {data.mapLink}
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ===== CONTACT US CREATE / EDIT ===== */
const ContactUsCreate = ({ data, token, setTcTab, onRefresh }) => {
  const [form, setForm] = useState({
    heading: data?.heading || "Contact Us",
    content: data?.content || "",
    email: data?.email || "",
    phone: data?.phone || "",
    whatsapp: data?.whatsapp || "",
    address: data?.address || "",
    mapLink: data?.mapLink || "",
    workingHours: data?.workingHours || "",
  });

  const handleSave = async () => {
    try {
      if (data?._id) {
        const response = await putWithTokenAPI(
          token,
          `/contact-us/update/${data._id}`,
          form
        );
        if (response?.statusCode === 200 || response?.status === "success") {
          toast.success("Contact Us updated successfully");
          onRefresh && onRefresh();
          setTcTab("preview");
        } else {
          toast.error(response?.message || "Failed to update Contact Us");
        }
      } else {
        const response = await postWithTokenAPI(
          token,
          "/contact-us/create",
          form
        );
        if (response?.statusCode === 200 || response?.status === "success") {
          toast.success("Contact Us created successfully");
          onRefresh && onRefresh();
          setTcTab("preview");
        } else {
          toast.error(response?.message || "Failed to create Contact Us");
        }
      }
    } catch (error) {
      console.error("Error updating Contact Us:", error);
      toast.error("Failed to update Contact Us");
    }
  };

  return (
    <div className="editor-container minimalist">
      <div className="tc-create-container">
        <h3>
          {data?._id ? "Edit Contact Us Details" : "Create Contact Us Details"}
        </h3>

        <form className="profiledataupdateform" style={{ maxWidth: "100%" }}>
          <div className="two-col">
            <div className="form-groupp">
              <label>Heading</label>
              <input
                type="text"
                value={form.heading}
                onChange={(e) => setForm({ ...form, heading: e.target.value })}
              />
            </div>
            <div className="form-groupp">
              <label>Working Hours</label>
              <input
                type="text"
                value={form.workingHours}
                onChange={(e) =>
                  setForm({ ...form, workingHours: e.target.value })
                }
              />
            </div>
          </div>

          <div className="two-col">
            <div className="form-groupp">
              <label>Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="form-groupp">
              <label>Phone Number</label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>

          <div className="two-col">
            <div className="form-groupp">
              <label>WhatsApp Number</label>
              <input
                type="text"
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              />
            </div>
            <div className="form-groupp">
              <label>Google Maps Link</label>
              <input
                type="text"
                value={form.mapLink}
                onChange={(e) => setForm({ ...form, mapLink: e.target.value })}
              />
            </div>
          </div>

          <div className="form-groupp">
            <label>Content Description</label>
            <textarea
              className="form-textarea"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
            />
          </div>

          <div className="form-groupp">
            <label>Address</label>
            <textarea
              className="form-textarea"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSave}
            >
              {data?._id ? "Update Contact Us" : "Save Contact Us"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ===== CONTACT US MANAGER ===== */
const ContactUsManager = ({ token, permission }) => {
  const [tcTab, setTcTab] = useState("preview");
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchContactData = async () => {
    setLoading(true);
    try {
      const response = await getWithTokenAPI(token, "/contact-us/list");
      if (response?.statusCode === 200 && response?.data) {
        const item = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        setContactData(item || null);
      } else {
        setContactData(null);
      }
    } catch (error) {
      console.error("Error fetching Contact Us:", error);
      setContactData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactData();
  }, [token]);

  return (
    <div className="tc-wrapper">
      <div className="tc-tabs">
        <div className="tc-tabs-child">
          <span
            className={tcTab === "preview" ? "tc-tab active" : "tc-tab"}
            onClick={() => setTcTab("preview")}
          >
            Preview
          </span>

          {permission?.logoandtermsCondition?.update && (
            <span
              className={tcTab === "create" ? "tc-tab active" : "tc-tab"}
              onClick={() => setTcTab("create")}
            >
              {contactData?._id ? "Edit" : "Create"}
            </span>
          )}
        </div>
      </div>

      <div className="tc-content">
        {loading ? (
          <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
        ) : tcTab === "preview" ? (
          <ContactUsListing data={contactData} />
        ) : (
          <ContactUsCreate
            data={contactData}
            token={token}
            setTcTab={setTcTab}
            onRefresh={fetchContactData}
          />
        )}
      </div>
    </div>
  );
};

/* ===== MAIN CMS MANAGEMENT PAGE ===== */
const CMSManagement = () => {
  const [activeTab, setActiveTab] = useState("logo");
  const auth = useSelector((state) => state.auth);
  const token = auth.token;
  const adminId = auth.userId;
  const permission = auth.permission;

  return (
    <div className="personalinfo" style={{ width: "98%", padding: "25px 30px" }}>
      <h2 style={{ marginBottom: "20px", fontWeight: "700", color: "#111827" }}>
        CMS Management
      </h2>

      {/* TABS HEADER */}
      <div className="tabs">
        {[
          "logo",
          "terms",
          "privacy",
          "cancellation",
          "shipping",
          "contact",
        ].map((tab) => (
          <span
            key={tab}
            className={activeTab === tab ? "active" : ""}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "logo"
              ? "Logo"
              : tab === "terms"
              ? "Terms & Condition"
              : tab === "privacy"
              ? "Privacy Policy"
              : tab === "cancellation"
              ? "Cancellation & Refund"
              : tab === "shipping"
              ? "Shipping & Exchange"
              : "Contact Us"}
          </span>
        ))}
      </div>

      {/* TAB CONTENT */}
      {activeTab === "logo" && (
        <LogoManager token={token} permission={permission} adminId={adminId} />
      )}

      {activeTab === "terms" && (
        <PolicyManager
          policyTitle="Terms & Conditions"
          endpoint="terms"
          token={token}
          permission={permission}
        />
      )}

      {activeTab === "privacy" && (
        <PolicyManager
          policyTitle="Privacy Policy"
          endpoint="privacy-policy"
          token={token}
          permission={permission}
        />
      )}

      {activeTab === "cancellation" && (
        <PolicyManager
          policyTitle="Cancellation & Refund Policy"
          endpoint="cancellation-refund-policy"
          token={token}
          permission={permission}
        />
      )}

      {activeTab === "shipping" && (
        <PolicyManager
          policyTitle="Shipping & Exchange Policy"
          endpoint="shipping-exchange-policy"
          token={token}
          permission={permission}
        />
      )}

      {activeTab === "contact" && (
        <ContactUsManager token={token} permission={permission} />
      )}
    </div>
  );
};

export default CMSManagement;
